// Import the pure types modules (not the package barrels) so this stays
// importable from Vitest without dragging the chat runtime in.
import { isAskUserFeedbackStagedAction } from './ask-user-feedback/types'
import { isReportToUserStagedAction } from './report-to-user/types'

/**
 * Pairs a background `AskUserFeedback` with the report card it is about, so the
 * question renders inside that card rather than as its own row.
 * Joined on `conversationId` — the feedback follow-up is enqueued into the
 * run's own conversation, so both rows carry it. HIG-5967.
 */

/** The fields pairing reads. Structural, so `StagedAction` satisfies it. */
export interface FeedbackPairingRow {
  id: string
  toolSet?: string
  toolName?: string
  conversationId?: string | null
  outputData?: unknown
}

function isHostOf(host: FeedbackPairingRow, feedback: FeedbackPairingRow): boolean {
  return (
    // Only report cards host. A draft against an integration carries an execute
    // footer, and a question under that reads as part of what is about to be sent.
    isReportToUserStagedAction(host) &&
    isAskUserFeedbackStagedAction(feedback) &&
    // A null conversation never matches: `report_to_user` is allowed to have
    // none, so pairing on absence would adopt any unattached feedback row.
    !!host.conversationId &&
    host.conversationId === feedback.conversationId
  )
}

/** Prefers an unanswered card, so the one still needing the user renders. */
export function findFeedbackForHost<T extends FeedbackPairingRow>(host: T, actions: ReadonlyArray<T>): T | undefined {
  if (!isReportToUserStagedAction(host)) return undefined
  let answered: T | undefined
  for (const action of actions) {
    if (!isHostOf(host, action)) continue
    if (action.outputData == null) return action
    answered ??= action
  }
  return answered
}

/**
 * Whether some host in `actions` renders this exact card. Asked through
 * `findFeedbackForHost` so the two cannot disagree: a host only renders one
 * card, so "a host exists" would hide a second unanswered one with nothing
 * showing it. Pass the VISIBLE list, which `feedback` must itself be in — a
 * card with no host on screen has nowhere to appear, and one the user can
 * never answer strands its run.
 */
export function hasFeedbackHost<T extends FeedbackPairingRow>(feedback: T, actions: ReadonlyArray<T>): boolean {
  if (!isAskUserFeedbackStagedAction(feedback)) return false
  return actions.some((host) => findFeedbackForHost(host, actions)?.id === feedback.id)
}
